import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import moment from "moment";
import { FilterRange, EnumHelper, ObjectHelper, LoanStatus, IdNameData, AccountActivationStage, AccountFilters, AccountQueryParams, DelinquentStatus } from 'common';
import {
  ProductName,
  AccountModificationFlagFilter,
  AccountModificationFlagFilterMap,
  AccountOfferPaymentFrequencies
} from '../account.model';
import { TenantService } from '../../admin/tenant/tenant.service';
import { AccountService } from "../account.service";
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readOnly } from '../../user/user-permission/user-permission.data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'ifb-account-list-filter-dialog',
    templateUrl: './account-list-filter-dialog.component.html',
    standalone: false
})
export class AccountListFilterDialogComponent implements OnInit {
  form: UntypedFormGroup;
  filters: AccountFilters;

  statusOptions = EnumHelper.getNamesAndValues(LoanStatus);
  delinquentStatusOptions = EnumHelper.getNamesAndValues(DelinquentStatus);
  activationStageOptions = EnumHelper.getNamesAndValues(AccountActivationStage);
  productTypeOptions = EnumHelper.getNamesAndValues(ProductName);
  paymentFrequencyOptions = EnumHelper.getNamesAndValues(AccountOfferPaymentFrequencies);
  modificationFlagOptions = EnumHelper.getMappedNamesAndValues(AccountModificationFlagFilter, AccountModificationFlagFilterMap);

  createdOnRange: FilterRange<moment.Moment> = {};
  renewalOnRange: FilterRange<moment.Moment> = {};
  upcomingPaymentOnRange: FilterRange<moment.Moment> = {};

  companies: IdNameData[] = [];
  facilities: IdNameData[] = [];
  canReadFacilities: boolean;

  constructor(
    public dialogRef: MatDialogRef<AccountListFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: UntypedFormBuilder,
    private tenantService: TenantService,
    private accountService: AccountService,
    private userPermissionService: UserPermissionService
  ) {

    this.filters = new AccountFilters(data.query);
    this.form = this.formBuilder.group({
      loanNumber: this.data.query.loanNumber || '',
      applicationNumber: this.data.query.applicationNumber || '',
      businessName: this.data.query.businessName || '',
      createdOn: this.data.query.createdOn || '',
      createdOnFrom: new UntypedFormControl(this.data.query.createdOnFrom ? new Date(this.data.query.createdOnFrom) : undefined, { validators: null, updateOn: 'blur' }),
      createdOnTo: new UntypedFormControl(this.data.query.createdOnTo ? new Date(this.data.query.createdOnTo) : undefined, { validators: null, updateOn: 'blur' }),
      renewalOn: this.data.query.renewalOn || '',
      renewalOnFrom: new UntypedFormControl(this.data.query.renewalOnFrom ? new Date(this.data.query.renewalOnFrom) : undefined, { validators: null, updateOn: 'blur' }),
      renewalOnTo: new UntypedFormControl(this.data.query.renewalOnTo ? new Date(this.data.query.renewalOnTo) : undefined, { validators: null, updateOn: 'blur' }),
      status: { value: ObjectHelper.makeArray(this.data.query.status)?.map(Number), disabled: false },
      delinquentStatus: { value: ObjectHelper.makeArray(this.data.query.delinquentStatus), disabled: false },
      activationStage: +this.data.query.activationStage >= 0 ? +this.data.query.activationStage : undefined,
      active: this.data.query.active || undefined,
      productCode: this.data.query.productCode || undefined,
      description: this.data.query.description || '',
      companyId: +this.data.query.companyId || undefined,
      facilityId: +this.data.query.facilityId || undefined,
      modificationFlags: { value: ObjectHelper.makeArray(this.data.query.modificationFlags) || '', disabled: false },
      minBalance: this.data.query.minBalance || undefined,
      maxBalance: this.data.query.maxBalance || undefined,
      minPaymentAmount: this.data.query.minPaymentAmount || undefined,
      maxPaymentAmount: this.data.query.maxPaymentAmount || undefined,
      paymentFrequency: +this.data.query.paymentFrequency >= 0 ? +this.data.query.paymentFrequency : undefined,
      upcomingPaymentDateOn: this.data.query.upcomingPaymentDateOn || '',
      minUpcomingPaymentDate: new UntypedFormControl(this.data.query.minUpcomingPaymentDate ? new Date(this.data.query.minUpcomingPaymentDate) : undefined, { validators: null, updateOn: 'blur' }),
      maxUpcomingPaymentDate:  new UntypedFormControl(this.data.query.maxUpcomingPaymentDate ? new Date(this.data.query.maxUpcomingPaymentDate) : undefined, { validators: null, updateOn: 'blur' })
    });
  }

  static show(dialog: MatDialog, query: AccountQueryParams, canFilterByEntity: boolean): Observable<any> {
    return dialog
      .open(AccountListFilterDialogComponent, {
        data: { query: query, canFilterByEntity: canFilterByEntity },
        closeOnNavigation: true,
      })
      .afterClosed();
  }

  ngOnInit(): void {
    this.createdOnRange.from = this.filters.createdOnFrom;
    this.createdOnRange.to = this.filters.createdOnTo;
    this.renewalOnRange.from = this.filters.renewalOnFrom;
    this.renewalOnRange.to = this.filters.renewalOnTo;
    this.upcomingPaymentOnRange.from = this.filters.minUpcomingPaymentDate;
    this.upcomingPaymentOnRange.to = this.filters.maxUpcomingPaymentDate;

    this.tenantService.names().subscribe(data => this.companies = data);
    this.userPermissionService 
      .granted([readOnly("admin-facilities")])
      .pipe(
        untilDestroyed(this), 
        tap(permission => this.canReadFacilities = permission),
        switchMap(permission => 
          permission 
            ? this.accountService.getFacilitiesOptions() 
            : of([] as IdNameData[])
        )
      )
      .subscribe(options => {
        this.facilities = options;
      });
  }


  createdDateSelected(dataRange: FilterRange<moment.Moment>): void {
    this.form.patchValue({
      createdOnFrom: dataRange.from,
      createdOnTo: dataRange.to
    });
  }

  renewalDateSelected(dataRange: FilterRange<moment.Moment>): void {
    this.form.patchValue({
      renewalOnFrom: dataRange.from,
      renewalOnTo: dataRange.to
    });
  }

  upcomingPaymentDateSelected(dataRange: FilterRange<moment.Moment>): void {
    this.form.patchValue({
      minUpcomingPaymentDate: dataRange.from,
      maxUpcomingPaymentDate: dataRange.to
    });
  }

  onConfirmed(formValues: unknown): void {
    this.dialogRef.close(new AccountFilters(formValues));
  }
}
